import {
  EditorCoursesRoute,
  EditorLessonsRoute,
  CommonTextsRoute,
  EditorLessonTemplatesRoute,
  DialogSpeakersRoute,
  WordAdminRoute,
  SentenceListPageRoute,
  VocabListsAdminRoute,
  UsersRoute,
  StaffMembersRoute,
  EditorTeachersRoute,
  MeetingBookingsAdminRoute,
  LessonBookingsAdminRoute,
  OrganizationListRoute,
  BannersRoute,
  BibliographyListRoute,
  CategoriesListRoute,
  CurriculaListRoute,
  EditBlockedEmailDomainsRoute,
} from 'app/constants/Routes';
import React, { FC } from 'react';
import { useI18n } from 'app/components/localization';
import RestrictContent from 'app/containers/auth/RestrictContent';
import {
  USER_MANAGEMENT_PERMISSIONS,
  STAFF_MANAGEMENT_PERMISSIONS,
  TEACHER_MANAGEMENT_PERMISSIONS,
  ADMIN_MEETING_BOOKINGS_PERMISSIONS,
  VIEW_LESSON_BOOKINGS_PERMISSIONS,
  VIEW_DICTIONARY_CONTENT_PERMISSIONS,
  EDIT_ORGANIZATION_PERMISSIONS,
  MATERIALS_ABILITY_GROUP,
  VIEW_EDITOR_LESSON_PERMISSIONS,
  EDIT_TEMPLATE_PERMISSIONS,
  VIEW_COMMON_TEXT_PERMISSIONS,
  VIEW_DIALOG_SPEAKER_LIST_PERMISSIONS,
  SITE_CONTENT_ABILITY_GROUP,
  EDIT_BANNER_PERMISSIONS,
  USE_RESERVED_FEATURES_ABILITY,
  EDIT_BLOCKED_DOMAINS_PERMISSIONS,
  VIEW_CURRICULA_PERMISSIONS,
} from 'app/constants/enums/Ability';
import Users from 'app/components/svgs/Users';
import Speaker from 'app/components/svgs/Speaker';
import Text from 'app/components/svgs/Text';
import Document from 'app/components/svgs/Document';
import CopyDocument from 'app/components/svgs/CopyDocument';
import UserSilhouette from 'app/components/svgs/UserSilhouette';
import Money from 'app/components/svgs/Money';
import SkullCrossBones from 'app/components/svgs/SkullCrossBones';
import NotifyTesterModal from 'app/components/admin/NotifyTesterModal';
import NavigationLink from 'app/components/header/navigation/NavigationLink';
import NavigationSection from 'app/components/header/navigation/NavigationSection';
import { isTeacher } from 'app/models/auth/selectors';
import { useAuthUser } from 'app/components/auth/AuthUserContext';
import { useDisplayLanguage } from 'app/components/LanguageContext';
import ModalNavigationLink from 'app/components/header/navigation/ModalNavigationLink';
import appConfig from 'app/config/client';
import { getTutorSiteUrl } from 'app/constants/TeacherSite';
import { useBrand } from 'app/components/OrganizationContext';

/**
 * A reusable component that renders common navigation items.
 * Should only be used in header's navigation items.
 */
const CommonNavigationItems: FC = () => {
  const { t } = useI18n();
  const user = useAuthUser();
  const displayLanguage = useDisplayLanguage();
  const brand = useBrand();

  return (
    <>
      {isTeacher(user) && (
        <NavigationSection>
          <NavigationLink icon={UserSilhouette} to={getTutorSiteUrl(appConfig.deployInfo)}>
            {t('admin.header.admin.tutor-site')}
          </NavigationLink>
        </NavigationSection>
      )}

      <RestrictContent allow={USER_MANAGEMENT_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink icon={Users} to={UsersRoute.getPathname()}>
            {t('admin.header.admin.users')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={EDIT_BLOCKED_DOMAINS_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink icon={Users} to={EditBlockedEmailDomainsRoute.getPathname()}>
            {t('admin.header.admin.blocked-email-domains')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={STAFF_MANAGEMENT_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink icon={Users} to={StaffMembersRoute.getPathname()}>
            {t('admin.header.admin.staff')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={TEACHER_MANAGEMENT_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink icon={Users} to={EditorTeachersRoute.getPathname()}>
            {t('admin.header.admin.teachers')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={ADMIN_MEETING_BOOKINGS_PERMISSIONS.concat(VIEW_LESSON_BOOKINGS_PERMISSIONS)}>
        <NavigationSection>
          <RestrictContent allow={ADMIN_MEETING_BOOKINGS_PERMISSIONS}>
            <NavigationLink icon={Speaker} to={MeetingBookingsAdminRoute.getPathname()}>
              {t('admin.header.admin.meeting-bookings')}
            </NavigationLink>
          </RestrictContent>

          <RestrictContent allow={VIEW_LESSON_BOOKINGS_PERMISSIONS}>
            <NavigationLink icon={Speaker} to={LessonBookingsAdminRoute.getPathname()}>
              {t('admin.header.admin.lesson-bookings')}
            </NavigationLink>
          </RestrictContent>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={VIEW_DICTIONARY_CONTENT_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink
            icon={Text}
            to={WordAdminRoute.getPathname({ languageId: displayLanguage.id })}
          >
            {t('admin.header.admin.global-words')}
          </NavigationLink>

          <NavigationLink
            icon={Text}
            to={SentenceListPageRoute.getPathname({ languageId: displayLanguage.id })}
          >
            {t('admin.header.admin.global-sentences')}
          </NavigationLink>

          <NavigationLink
            icon={Text}
            to={VocabListsAdminRoute.getPathname({ languageId: displayLanguage.id })}
          >
            {t('admin.header.admin.vocab-lists')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={EDIT_ORGANIZATION_PERMISSIONS}>
        <NavigationSection>
          <NavigationLink icon={Document} to={OrganizationListRoute.getPathname()}>
            {t('admin.header.admin.organizations')}
          </NavigationLink>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={MATERIALS_ABILITY_GROUP.abilities}>
        <NavigationSection>
          <RestrictContent allow={VIEW_CURRICULA_PERMISSIONS}>
            <NavigationLink
              icon={Document}
              to={CurriculaListRoute.getPathname({
                organizationId: brand.organization,
              })}
            >
              {t('admin.header.admin.curricula')}
            </NavigationLink>
          </RestrictContent>

          <NavigationLink
            icon={Document}
            to={CategoriesListRoute.getPathname({
              languageId: displayLanguage.id,
            })}
          >
            {t('admin.header.admin.categories')}
          </NavigationLink>

          <NavigationLink
            icon={Document}
            to={EditorCoursesRoute.getPathname({ languageId: displayLanguage.id })}
          >
            {t('admin.header.admin.courses')}
          </NavigationLink>

          <RestrictContent allow={VIEW_EDITOR_LESSON_PERMISSIONS}>
            <NavigationLink
              icon={Document}
              to={EditorLessonsRoute.getPathname({ languageId: displayLanguage.id })}
            >
              {t('admin.header.admin.lessons')}
            </NavigationLink>

            <NavigationLink
              icon={Document}
              to={BibliographyListRoute.getPathname()}
            >
              {t('admin.header.admin.bibliographies')}
            </NavigationLink>
          </RestrictContent>

          <RestrictContent allow={EDIT_TEMPLATE_PERMISSIONS}>
            <NavigationLink
              icon={CopyDocument}
              to={EditorLessonTemplatesRoute.getPathname({ languageId: displayLanguage.id })}
            >
              {t('admin.header.admin.lesson-templates')}
            </NavigationLink>
          </RestrictContent>

          <RestrictContent allow={VIEW_COMMON_TEXT_PERMISSIONS}>
            <NavigationLink
              icon={Text}
              to={CommonTextsRoute.getPathname({ languageId: displayLanguage.id })}
            >
              {t('admin.header.admin.common-texts')}
            </NavigationLink>
          </RestrictContent>

          <RestrictContent allow={VIEW_DIALOG_SPEAKER_LIST_PERMISSIONS}>
            <NavigationLink
              icon={UserSilhouette}
              to={DialogSpeakersRoute.getPathname({ languageId: displayLanguage.id })}
            >
              {t('admin.header.admin.dialog-speakers')}
            </NavigationLink>
          </RestrictContent>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={SITE_CONTENT_ABILITY_GROUP.abilities}>
        <NavigationSection>
          <RestrictContent allow={EDIT_BANNER_PERMISSIONS}>
            <NavigationLink icon={Money} to={BannersRoute.getPathname()}>
              Banners
            </NavigationLink>
          </RestrictContent>
        </NavigationSection>
      </RestrictContent>

      <RestrictContent allow={[USE_RESERVED_FEATURES_ABILITY]}>
        <NavigationSection>
          <ModalNavigationLink
            icon={SkullCrossBones}
            renderModal={({ onClose }) => <NotifyTesterModal onClose={onClose} />}
          >
            Send Error
          </ModalNavigationLink>
        </NavigationSection>
      </RestrictContent>

      <NavigationSection>
        <LegalLinks />
      </NavigationSection>
    </>
  );
};

const LegalLinks: FC = () => {
  const brand = useBrand();
  const { t } = useI18n();

  return (
    <>
      {brand.brandLinks.map(({ href, getName }) => (
        <NavigationLink key={href} to={href} target="_blank">
          {getName(t)}
        </NavigationLink>
      ))}
    </>
  );
};

export default CommonNavigationItems;
